import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  Medal,
  Crown,
  Flame,
  TrendingUp,
  BookOpen,
  Zap,
  Search,
  User,
  Timer,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { gamificationApi } from "../lib";

interface LeaderboardUser {
  _id: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  username?: string;
}

interface LeaderboardEntry {
  _id: string;
  user: LeaderboardUser;
  totalPoints: number;
  level: number;
  badgesCount: number;
  streakDays: number;
  coursesCompleted: number;
  quizzesPassed: number;
  rank: number;
}

type TimePeriod = "weekly" | "monthly" | "all";
type CategoryFilter = "overall" | "points" | "level" | "streak" | "completion";

const PAGE_SIZE = 10;

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("all");
  const [categoryFilter, setCategoryFilter] =
    useState<CategoryFilter>("overall");
  const [page, setPage] = useState(1);
  const { user } = useAuth();

  useEffect(() => {
    fetchLeaderboard();
  }, [timePeriod, categoryFilter]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const category = categoryFilter === "overall" ? "points" : categoryFilter;
      const response = await gamificationApi.getLeaderboard(200, category);
      const rawData =
        (response as any)?.data?.data || (response as any)?.data || [];
      const normalised = rawData.map((item: any) => {
        const level = item?.level;
        const normalisedLevel =
          typeof level === "object" && level !== null
            ? Number(level.level ?? 1)
            : typeof level === "number"
              ? level
              : 1;
        return {
          ...item,
          level: normalisedLevel,
        } as LeaderboardEntry;
      });
      setEntries(normalised);
      setPage(1);
    } catch (err) {
      console.error("Failed to fetch leaderboard:", err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = entries.filter((entry) => {
    const fullName =
      `${entry.user?.firstName || ""} ${entry.user?.lastName || ""}`.toLowerCase();
    const username = (entry.user?.username || "").toLowerCase();
    const query = searchQuery.toLowerCase();
    return fullName.includes(query) || username.includes(query);
  });

  const rankedEntries = entries.map((entry, idx) => ({
    ...entry,
    rank: idx + 1,
  }));
  const rankedFiltered = filteredEntries.map((entry, idx) => ({
    ...entry,
    rank: entries.findIndex((e) => e._id === entry._id) + 1,
  }));

  const totalPages = Math.max(1, Math.ceil(rankedFiltered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageEntries = rankedFiltered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const topThree = rankedFiltered.slice(0, 3);
  const currentUserEntry = rankedEntries.find((e) => e.user?._id === user?._id);

  const getDisplayedLevel = (entry: LeaderboardEntry) => {
    const raw = (entry as any).level;
    if (raw && typeof raw === "object") return raw.level ?? 1;
    return typeof raw === "number" ? raw : 1;
  };

  const getMedalColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "from-yellow-400 to-amber-500";
      case 2:
        return "from-gray-300 to-gray-400";
      case 3:
        return "from-orange-400 to-orange-500";
      default:
        return "from-gray-100 to-gray-200";
    }
  };

  const getUserInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return "?";
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const getUserDisplayName = (entry: LeaderboardEntry) => {
    const first = entry.user?.firstName || "";
    const last = entry.user?.lastName || "";
    const full = `${first} ${last}`.trim();
    if (full) return full;
    const name = entry.user?.username || "";
    if (name) return name;
    const username = entry.user?.username || "";
    if (username) return username;
    return `User #${entry.rank}`;
  };

  const getCategoryLabel = (category: CategoryFilter) => {
    switch (category) {
      case "overall":
        return "Overall";
      case "points":
        return "Points";
      case "level":
        return "Level";
      case "streak":
        return "Streak";
      case "completion":
        return "Completion";
      default:
        return category;
    }
  };

  const getCategoryIcon = (category: CategoryFilter) => {
    switch (category) {
      case "overall":
        return <TrendingUp className="w-3 h-3" />;
      case "points":
        return <Zap className="w-3 h-3" />;
      case "level":
        return <BookOpen className="w-3 h-3" />;
      case "streak":
        return <Flame className="w-3 h-3" />;
      case "completion":
        return <Timer className="w-3 h-3" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-2 border-2 border-orange-500 rounded-full border-t-transparent animate-spin" />
          <p className="text-[10px] text-gray-500">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      <div className="px-4 mx-auto max-w-7xl md:px-8 lg:px-12 xl:px-16">
        <div className="mb-5">
          <button
            onClick={() => navigate("/dashboard/badges")}
            className="flex items-center gap-1.5 mb-3 text-orange-600 hover:text-orange-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[10px] font-medium">Back to Badges</span>
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 shadow-sm">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Leaderboard</h1>
                <p className="text-[10px] text-gray-500">
                  Top learners ranked by performance
                </p>
              </div>
            </div>

            {currentUserEntry && (
              <div className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-lg shadow-sm">
                <User className="w-3 h-3 text-gray-500" />
                <span className="text-[9px] font-semibold text-gray-700">
                  #{currentUserEntry.rank}
                </span>
              </div>
            )}
          </div>

          <div className="relative mt-3">
            <Search className="absolute w-3.5 h-3.5 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-2 pl-9 pr-4 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
            />
          </div>

          <div className="flex gap-2 mt-3">
            {(["all", "weekly", "monthly"] as TimePeriod[]).map((period) => (
              <button
                key={period}
                onClick={() => setTimePeriod(period)}
                className={`flex-1 px-3 py-1.5 text-[10px] font-medium rounded-lg transition-all duration-200 ${
                  timePeriod === period
                    ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                {period === "all" ? "All-Time" : period}
              </button>
            ))}
          </div>

          <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
            {(
              [
                "overall",
                "points",
                "level",
                "streak",
                "completion",
              ] as CategoryFilter[]
            ).map((category) => (
              <button
                key={category}
                onClick={() => setCategoryFilter(category)}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium rounded-lg whitespace-nowrap transition-all duration-200 ${
                  categoryFilter === category
                    ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                {getCategoryIcon(category)}
                <span className="hidden sm:inline">
                  {getCategoryLabel(category)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {filteredEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-center w-16 h-16 mb-3 bg-gray-100 rounded-full">
              <Trophy className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-gray-900">
              No rankings found
            </h3>
            <p className="text-[10px] text-gray-500 max-w-xs">
              {searchQuery
                ? "No users match your search. Try different keywords."
                : "Start learning to appear on the leaderboard!"}
            </p>
          </div>
        ) : (
          <>
            {/* Podium Section - Top 3 - Centered with max-width */}
            {topThree.length >= 1 && (
              <div className="flex items-end justify-center gap-3 mb-8 md:gap-6 lg:gap-8">
                {/* 2nd Place */}
                {topThree[1] && (
                  <div className="flex-1 max-w-[140px] md:max-w-[180px] lg:max-w-[200px]">
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div
                        className={`h-1 md:h-1.5 bg-gradient-to-r ${getMedalColor(2)}`}
                      />
                      <div className="p-2 md:p-3 lg:p-4">
                        <div className="flex flex-col items-center">
                          <div className="relative mb-1 md:mb-2">
                            <div
                              className={`flex items-center justify-center w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-full bg-gradient-to-br ${getMedalColor(2)} shadow-md`}
                            >
                              {topThree[1].user?.avatar ? (
                                <img
                                  src={topThree[1].user.avatar}
                                  alt=""
                                  className="object-cover w-full h-full rounded-full"
                                />
                              ) : (
                                <span className="text-base md:text-xl lg:text-2xl font-bold text-white">
                                  {getUserInitials(
                                    topThree[1].user?.firstName,
                                    topThree[1].user?.lastName,
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="absolute -top-1 -right-1 bg-gray-800 text-white text-[9px] md:text-[10px] lg:text-xs font-bold w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center shadow">
                              2
                            </div>
                          </div>
                          <h3 className="text-[10px] md:text-xs lg:text-sm font-bold text-gray-900 truncate max-w-[100px] md:max-w-[120px] lg:max-w-[140px]">
                             {getUserDisplayName(topThree[1])}
                          </h3>
                          <p className="text-[8px] md:text-[9px] lg:text-[10px] text-gray-500 mt-0.5">
                            Lvl {getDisplayedLevel(topThree[1])}
                          </p>
                          <div className="flex items-center gap-0.5 md:gap-1 mt-1 md:mt-1.5">
                            <Zap className="w-2.5 h-2.5 md:w-3 md:h-3 lg:w-3.5 lg:h-3.5 text-amber-500" />
                            <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900">
                              {topThree[1].totalPoints.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-full py-1.5 md:py-2 lg:py-3 bg-gradient-to-t from-gray-100 to-gray-50 border border-gray-200 border-t-0 rounded-b-xl">
                      <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-700">
                        {topThree[1].totalPoints.toLocaleString()} pts
                      </span>
                    </div>
                  </div>
                )}

                {/* 1st Place */}
                {topThree[0] && (
                  <div className="flex-1 max-w-[160px] md:max-w-[200px] lg:max-w-[220px] -mt-2 md:-mt-4 lg:-mt-6">
                    <div className="bg-white border-2 border-yellow-200 rounded-xl shadow-md overflow-hidden">
                      <div
                        className={`h-1.5 md:h-2 lg:h-2.5 bg-gradient-to-r ${getMedalColor(1)}`}
                      />
                      <div className="p-3 md:p-4 lg:p-5">
                        <div className="flex flex-col items-center">
                          <div className="relative mb-1 md:mb-2 lg:mb-3">
                            <div
                              className={`flex items-center justify-center w-14 h-14 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br ${getMedalColor(1)} shadow-lg ring-2 md:ring-4 ring-amber-100`}
                            >
                              {topThree[0].user?.avatar ? (
                                <img
                                  src={topThree[0].user.avatar}
                                  alt=""
                                  className="object-cover w-full h-full rounded-full"
                                />
                              ) : (
                                <span className="text-xl md:text-2xl lg:text-3xl font-bold text-white">
                                  {getUserInitials(
                                    topThree[0].user?.firstName,
                                    topThree[0].user?.lastName,
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="absolute -top-2 -right-2 bg-amber-500 text-white p-0.5 md:p-1 rounded-full shadow-md">
                              <Crown className="w-3.5 h-3.5 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                            </div>
                          </div>
                          <h3 className="text-[11px] md:text-sm lg:text-base font-bold text-gray-900 truncate max-w-[110px] md:max-w-[140px] lg:max-w-[160px]">
                             {getUserDisplayName(topThree[0])}
                          </h3>
                          <p className="text-[8px] md:text-[10px] lg:text-xs text-gray-500 mt-0.5">
                            Lvl {getDisplayedLevel(topThree[0])}
                          </p>
                          <div className="flex items-center gap-0.5 md:gap-1 mt-1 md:mt-1.5 lg:mt-2">
                            <Zap className="w-3 h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 text-amber-500" />
                            <span className="text-[10px] md:text-xs lg:text-sm font-bold text-gray-900">
                              {topThree[0].totalPoints.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-full py-2 md:py-3 lg:py-4 bg-gradient-to-t from-gray-200 to-gray-100 border border-gray-300 border-t-0 rounded-b-xl">
                      <span className="text-[10px] md:text-xs lg:text-sm font-bold text-gray-900">
                        {topThree[0].totalPoints.toLocaleString()} pts
                      </span>
                    </div>
                  </div>
                )}

                {/* 3rd Place */}
                {topThree[2] && (
                  <div className="flex-1 max-w-[140px] md:max-w-[180px] lg:max-w-[200px]">
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div
                        className={`h-1 md:h-1.5 bg-gradient-to-r ${getMedalColor(3)}`}
                      />
                      <div className="p-2 md:p-3 lg:p-4">
                        <div className="flex flex-col items-center">
                          <div className="relative mb-1 md:mb-2">
                            <div
                              className={`flex items-center justify-center w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-full bg-gradient-to-br ${getMedalColor(3)} shadow-md`}
                            >
                              {topThree[2].user?.avatar ? (
                                <img
                                  src={topThree[2].user.avatar}
                                  alt=""
                                  className="object-cover w-full h-full rounded-full"
                                />
                              ) : (
                                <span className="text-base md:text-xl lg:text-2xl font-bold text-white">
                                  {getUserInitials(
                                    topThree[2].user?.firstName,
                                    topThree[2].user?.lastName,
                                  )}
                                </span>
                              )}
                            </div>
                            <div className="absolute -top-1 -right-1 bg-orange-600 text-white text-[9px] md:text-[10px] lg:text-xs font-bold w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center shadow">
                              3
                            </div>
                          </div>
                          <h3 className="text-[10px] md:text-xs lg:text-sm font-bold text-gray-900 truncate max-w-[100px] md:max-w-[120px] lg:max-w-[140px]">
                             {getUserDisplayName(topThree[2])}
                          </h3>
                          <p className="text-[8px] md:text-[9px] lg:text-[10px] text-gray-500 mt-0.5">
                            Lvl {getDisplayedLevel(topThree[2])}
                          </p>
                          <div className="flex items-center gap-0.5 md:gap-1 mt-1 md:mt-1.5">
                            <Zap className="w-2.5 h-2.5 md:w-3 md:h-3 lg:w-3.5 lg:h-3.5 text-amber-500" />
                            <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-900">
                              {topThree[2].totalPoints.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-full py-1.5 md:py-2 lg:py-3 bg-gradient-to-t from-gray-100 to-gray-50 border border-gray-200 border-t-0 rounded-b-xl">
                      <span className="text-[9px] md:text-[10px] lg:text-xs font-bold text-gray-700">
                        {topThree[2].totalPoints.toLocaleString()} pts
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Current User Rank Card */}
            {currentUserEntry &&
              !topThree.find((e) => e.user?._id === user?._id) && (
                <div className="mb-4 p-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-100 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-7 h-7 bg-orange-500 text-white font-bold text-[10px] rounded-full">
                        #{currentUserEntry.rank}
                      </div>
                      <div className="flex items-center gap-2">
                        {currentUserEntry.user?.avatar ? (
                          <img
                            src={currentUserEntry.user.avatar}
                            alt=""
                            className="object-cover w-7 h-7 rounded-full ring-2 ring-orange-300"
                          />
                        ) : (
                          <div className="flex items-center justify-center w-7 h-7 bg-orange-500 text-white text-[9px] rounded-full">
                            {getUserInitials(
                              currentUserEntry.user?.firstName,
                              currentUserEntry.user?.lastName,
                            )}
                          </div>
                        )}
                        <div>
                          <p className="text-[10px] font-semibold text-gray-900">
                            {getUserDisplayName(currentUserEntry)}
                          </p>
                          <p className="text-[8px] text-gray-500">
                            Level {getDisplayedLevel(currentUserEntry)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Zap className="w-3 h-3 text-amber-500" />
                      <span className="text-xs font-bold text-gray-900">
                        {currentUserEntry.totalPoints.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

            {/* Leaderboard Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-12 px-3 py-2 bg-gray-50 border-b border-gray-100 text-[8px] font-semibold text-gray-500 uppercase tracking-wider">
                <div className="col-span-2 text-center">#</div>
                <div className="col-span-6">Learner</div>
                <div className="col-span-2 text-center">Lvl</div>
                <div className="col-span-2 text-right">Points</div>
              </div>

              <div className="divide-y divide-gray-50">
                {pageEntries.length === 0 ? (
                  <div className="py-6 text-center text-[9px] text-gray-500">
                    No users on this page
                  </div>
                ) : (
                  pageEntries.map((entry) => {
                    const isCurrentUser = entry.user?._id === user?._id;
                    return (
                      <div
                        key={entry._id}
                        className={`grid grid-cols-12 px-3 py-2 items-center transition-colors ${
                          isCurrentUser ? "bg-orange-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="col-span-2 flex justify-center">
                          <div
                            className={`text-[9px] font-bold ${entry.rank <= 10 ? "text-gray-700" : "text-gray-400"}`}
                          >
                            {entry.rank}
                          </div>
                        </div>
                        <div className="col-span-6 flex items-center gap-2">
                          {entry.user?.avatar ? (
                            <img
                              src={entry.user.avatar}
                              alt=""
                              className="object-cover w-6 h-6 rounded-full ring-1 ring-gray-200"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-6 h-6 bg-gradient-to-br from-orange-100 to-orange-200 text-orange-600 text-[8px] font-bold rounded-full">
                              {getUserInitials(
                                entry.user?.firstName,
                                entry.user?.lastName,
                              )}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-[9px] font-medium text-gray-900 truncate">
                              {(() => {
                                const first = entry.user?.firstName || "";
                                const last = entry.user?.lastName || "";
                                const full = `${first} ${last}`.trim();
                                return full || entry.user?.name || entry.user?.username || `User #${entry.rank}`;
                              })()}
                            </p>
                          </div>
                        </div>
                        <div className="col-span-2 text-center">
                          <span className="inline-block px-1 py-0.5 text-[8px] font-semibold text-orange-600 bg-orange-50 rounded">
                            {getDisplayedLevel(entry)}
                          </span>
                        </div>
                        <div className="col-span-2 text-right">
                          <div className="flex items-center justify-end gap-0.5">
                            <Zap className="w-2 h-2 text-amber-500" />
                            <span className="text-[9px] font-bold text-gray-900">
                              {entry.totalPoints.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <button
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Prev
                </button>
                <span className="text-[10px] text-gray-500">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
                <TrendingUp className="w-4 h-4 mx-auto mb-0.5 text-orange-500" />
                <p className="text-base font-bold text-gray-900">
                  {filteredEntries.length}
                </p>
                <p className="text-[7px] text-gray-500">Learners</p>
              </div>
              <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
                <Zap className="w-4 h-4 mx-auto mb-0.5 text-amber-500" />
                <p className="text-base font-bold text-gray-900">
                  {filteredEntries
                    .reduce((sum, e) => sum + (e.totalPoints || 0), 0)
                    .toLocaleString()}
                </p>
                <p className="text-[7px] text-gray-500">Total Pts</p>
              </div>
              <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
                <BookOpen className="w-4 h-4 mx-auto mb-0.5 text-purple-500" />
                <p className="text-base font-bold text-gray-900">
                  {filteredEntries.reduce(
                    (sum, e) => sum + (getDisplayedLevel(e) || 0),
                    0,
                  )}
                </p>
                <p className="text-[7px] text-gray-500">Levels</p>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default LeaderboardPage;
